import { Table, Divider } from 'antd'
import { SortOrder } from 'antd/lib/table'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerAvatarPlus } from '../../components/custom_components/AvatarPlus'
import { PerEmpty } from '../../components/custom_components/Empty'
import { NameDisplayPreference } from '../../graphql/enum/name-display-preference.enum'
import { computeFullname } from '../../shared/nameDisplayPreferencesUtil'
import { EmployeeWithLatestAndDraftEvaluation, Country, EmployeeLevelEnum, ScoreGoalType, IdvWeightDto, RoleName } from '../../types'
import { getSortOrderByEmployeeLevel } from '../companySettings/employees/employeesLevelSorterUtil'
import { useAuth } from '../../context/auth-context'
import { useEmployeesWithLatestAndDraftScoreGrid } from '../../graphql/draft-score-grid/useDraftScoreGrid'
import { LoadingAndErrors } from '../../components/custom_components/LoadingAndErrors'
import PerSpacer from '../../components/custom_components/Spacer'
import BackgroundBox from '../../components/custom_components/BackgroundBox'
import PerFlexRow from '../../components/custom_components/FlexRow'
import PerButton from '../../components/custom_components/Button'
import { DraftScoreGridsModal } from './DraftScoreGridsModal'
import { ExcelManager } from '../../shared/xlsx/ExcelManager'
import { useScoreGridsStore } from './ScoreGridsStore'
import shallow from 'zustand/shallow'
import { DraftScoreGridsImportModal } from './DraftScoreGridsImportModal'
import classNames from 'classnames'
import PerIcon from '../../components/custom_components/Icon'
import { LatestScoreGridsModal } from './LatestScoreGridsModal'
import { PerTableActionButtons } from '../../components/custom_components/TableActionButtons'
import { useUserManagersRolesByCompany } from '../../graphql/company/hooks/useUserManagerRolesByCompany'

type DraftScoreGridsTableProps = {
  companyId: string
  country: Country
  expanded: boolean
}

export const DraftScoreGridsTable: FC<DraftScoreGridsTableProps> = ({ companyId, country, expanded }) => {
  const { user } = useAuth()
  const [t] = useTranslation()
  const [selected, companiesAndCountriesSelected, isProcessingExport, setIsProcessingExport] = useScoreGridsStore(
    (state) => [state.selected, state.companiesAndCountriesSelected, state.isProcessingExport, state.setIsProcessingExport],
    shallow,
  )
  const [employeeData, setEmployeeData] = useState<EmployeeWithLatestAndDraftEvaluation | undefined>(undefined)
  const [latestEvaluationModalVisible, setLatestEvaluationModalVisible] = useState<boolean>(false)
  const [draftEvaluationModalVisible, setDraftEvaluationModalVisible] = useState<boolean>(false)
  const [isImportModalOpened, setIsImportModalOpened] = useState<boolean>(false)

  const { data, error, loading } = useEmployeesWithLatestAndDraftScoreGrid(companyId, country)
  const { data: userRolesData } = useUserManagersRolesByCompany(companyId)
  const userRoles: RoleName[] =
    userRolesData && userRolesData.findUserManagerRolesByCompany ? userRolesData.findUserManagerRolesByCompany : []
  const isCompanyManager = userRoles.includes(RoleName.CompanyManager)

  if (
    loading ||
    error ||
    !data ||
    !data.getEmployeesWithLatestAndDraftScoreGrid ||
    !data.getCountryEmployeeLevels ||
    !data.getCountrySettings ||
    !data.getIdVWeightsByCountry
  ) {
    return (
      <LoadingAndErrors
        isLoading={loading}
        hasNoData={
          !data ||
          !data.getEmployeesWithLatestAndDraftScoreGrid ||
          !data.getCountryEmployeeLevels ||
          !data.getCountrySettings ||
          !!data.getIdVWeightsByCountry
        }
        error={error}
        showSpinner
      />
    )
  }

  const tableData: EmployeeWithLatestAndDraftEvaluation[] = data.getEmployeesWithLatestAndDraftScoreGrid
  const employeeLevels: EmployeeLevelEnum[] = data.getCountryEmployeeLevels
  const isIdVWeightEnabled: boolean = data.getCountrySettings.isIdVWeightEnabled
  const weights: IdvWeightDto[] = data.getIdVWeightsByCountry

  const columns = [
    {
      key: 'employee',
      width: '30%',
      title: t('draftScoreGrid.table.employee'),
      sorter: (employeeA: EmployeeWithLatestAndDraftEvaluation, employeeB: EmployeeWithLatestAndDraftEvaluation) => {
        const nameA = computeFullname(user.nameDisplayPreference, employeeA.employee.firstName, employeeA.employee.lastName)
        const nameB = computeFullname(user.nameDisplayPreference, employeeB.employee.firstName, employeeB.employee.lastName)
        return nameA.localeCompare(nameB)
      },
      render: (tableData: EmployeeWithLatestAndDraftEvaluation) => (
        <PerAvatarPlus
          nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
          employeeId={tableData.employee.id}
          firstName={tableData.employee.firstName}
          lastName={tableData.employee.lastName}
          infoText={tableData.employee.companyName}
          linkToProfile={
            tableData.employee.id !== user.employeeId
              ? {
                  resigned: false,
                }
              : undefined
          }
          tooltip={tableData.employee.id !== user.employeeId ? t('draftScoreGrid.table.viewProfile') : undefined}
        />
      ),
    },
    {
      key: 'level',
      title: t('draftScoreGrid.table.level'),
      width: '10%',
      defaultSortOrder: 'ascend' as SortOrder,
      sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
      sorter: (employeeA: EmployeeWithLatestAndDraftEvaluation, employeeB: EmployeeWithLatestAndDraftEvaluation) => {
        const levelA = getSortOrderByEmployeeLevel(employeeA.employee.currentLevel as EmployeeLevelEnum, employeeLevels)
        const levelB = getSortOrderByEmployeeLevel(employeeB.employee.currentLevel as EmployeeLevelEnum, employeeLevels)
        return levelA - levelB
      },
      render: (tableData: EmployeeWithLatestAndDraftEvaluation) => tableData.employee.currentLevel,
    },
    {
      key: 'bu',
      title: t('draftScoreGrid.table.bu'),
      width: '10%',
      sorter: (employeeA: EmployeeWithLatestAndDraftEvaluation, employeeB: EmployeeWithLatestAndDraftEvaluation) => {
        return employeeA.employee.buName.localeCompare(employeeB.employee.buName)
      },
      render: (tableData: EmployeeWithLatestAndDraftEvaluation) => tableData.employee.buName,
    },
    {
      key: 'latestEvaluation',
      title: t('draftScoreGrid.table.latestEvaluation'),
      width: '18%',
      className: 'textAlign-right',
      sorter: (
        employeeA: EmployeeWithLatestAndDraftEvaluation,
        employeeB: EmployeeWithLatestAndDraftEvaluation,
        sortDirection: SortOrder | undefined,
      ) => {
        const resultScoreA = employeeA.existingLatestEvaluation ? (employeeA.latestEvaluation ? employeeA.latestEvaluation : 1) : 0
        const resultScoreB = employeeB.existingLatestEvaluation ? (employeeB.latestEvaluation ? employeeB.latestEvaluation : 1) : 0
        const comparison = resultScoreA - resultScoreB
        if (comparison === 0) {
          const fullNameA = computeFullname(user.nameDisplayPreference, employeeA.employee.firstName, employeeA.employee.lastName)
          const fullNameB = computeFullname(user.nameDisplayPreference, employeeB.employee.firstName, employeeB.employee.lastName)
          return sortDirection === 'ascend' ? fullNameA.localeCompare(fullNameB) : fullNameB.localeCompare(fullNameA)
        }
        return comparison
      },
      render: (tableData: EmployeeWithLatestAndDraftEvaluation) => (
        <>
          {tableData.existingLatestEvaluation ? (
            tableData.latestEvaluation ? (
              tableData.latestEvaluation.toFixed(2)
            ) : (
              <PerIcon name="check_stroke" color="success" variant="decoration" size={20} />
            )
          ) : (
            '-'
          )}
        </>
      ),
    },
    {
      key: 'draftEvaluation',
      title: t('draftScoreGrid.table.draftEvaluation'),
      width: '18%',
      className: 'textAlign-right',
      sorter: (
        employeeA: EmployeeWithLatestAndDraftEvaluation,
        employeeB: EmployeeWithLatestAndDraftEvaluation,
        sortDirection: SortOrder | undefined,
      ) => {
        const resultScoreA = employeeA.existingDraftEvaluation ? (employeeA.draftEvaluation ? employeeA.draftEvaluation : 1) : 0
        const resultScoreB = employeeB.existingDraftEvaluation ? (employeeB.draftEvaluation ? employeeB.draftEvaluation : 1) : 0
        const comparison = resultScoreA - resultScoreB
        if (comparison === 0) {
          const fullNameA = computeFullname(user.nameDisplayPreference, employeeA.employee.firstName, employeeA.employee.lastName)
          const fullNameB = computeFullname(user.nameDisplayPreference, employeeB.employee.firstName, employeeB.employee.lastName)
          return sortDirection === 'ascend' ? fullNameA.localeCompare(fullNameB) : fullNameB.localeCompare(fullNameA)
        }
        return comparison
      },
      render: (tableData: EmployeeWithLatestAndDraftEvaluation) => (
        <>
          <>
            {tableData.existingDraftEvaluation ? (
              tableData.draftEvaluation ? (
                tableData.draftEvaluation.toFixed(2)
              ) : (
                <PerIcon name="check_stroke" color="success" variant="decoration" size={20} />
              )
            ) : (
              '-'
            )}
          </>
        </>
      ),
    },
    {
      key: 'space',
      width: '2%',
    },
    {
      key: 'actions',
      title: t('draftScoreGrid.table.actions'),
      width: '12%',
      render: (tableData: EmployeeWithLatestAndDraftEvaluation) => {
        return (
          <PerTableActionButtons
            justify="start"
            variant="pastel"
            buttons={[
              {
                onClick: (e: any) => {
                  e.preventDefault()
                  setLatestEvaluationModalVisible(true)
                  setEmployeeData(tableData)
                },
                color: 'primary',
                icon: 'view_fill',
                tooltip: !!tableData.existingLatestEvaluation
                  ? t('draftScoreGrid.table.latestEvaluationTooltip')
                  : t('draftScoreGrid.table.missingLastConfirmedEvaluationTooltip', {
                      employeeFullName: computeFullname(
                        user.nameDisplayPreference,
                        tableData.employee.firstName,
                        tableData.employee.lastName,
                      ),
                    }),
                disabled: !tableData.existingLatestEvaluation,
              },
              {
                onClick: (e: any) => {
                  e.preventDefault()
                  setDraftEvaluationModalVisible(true)
                  setEmployeeData(tableData)
                },
                color: 'primary',
                icon: 'edit_fill',
                tooltip: isCompanyManager
                  ? t('draftScoreGrid.table.editTooltip')
                  : !!tableData.existingDraftEvaluation
                  ? t('draftScoreGrid.table.viewTooltip')
                  : t('draftScoreGrid.table.missingDraftEvaluationTooltip', {
                      employeeFullName: computeFullname(
                        user.nameDisplayPreference,
                        tableData.employee.firstName,
                        tableData.employee.lastName,
                      ),
                    }),
                disabled: isCompanyManager ? isProcessingExport : !tableData.existingDraftEvaluation,
              },
            ]}
          />
        )
      },
    },
  ]

  const scoreGoalTypes = Object.keys(ScoreGoalType) as ScoreGoalType[]
  const headerColumnsNames: string[] = [
    t('draftScoreGrid.excel.fileColumnNames.id'),
    t('draftScoreGrid.excel.fileColumnNames.firstName'),
    t('draftScoreGrid.excel.fileColumnNames.lastName'),
    t('draftScoreGrid.excel.fileColumnNames.bu'),
    t('draftScoreGrid.excel.fileColumnNames.title'),
    isIdVWeightEnabled ? t('draftScoreGrid.excel.fileColumnNames.evaluationLastConfirmed') : '',
    isIdVWeightEnabled ? t('draftScoreGrid.excel.fileColumnNames.evaluationNewProposal') : '',
    ...scoreGoalTypes.map((type) => t(`draftScoreGrid.excel.fileColumnNames.scoreGoal.${type}`)),
  ]
  const weightsColList = ['H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y']

  const downloadExcel = async () => {
    const company = companiesAndCountriesSelected.find((el) => el.value === selected.companyId)!.label
    const country = selected.country

    const filename = `Evaluation Grids - ${company} - ${country}.xlsx`

    setIsProcessingExport(true)

    // original data
    const data = mapEmployeeWithScoreGridToArray(tableData)
    const wsName = String(country)
    const dataWithFormulae = isIdVWeightEnabled ? addFormulaeToData(data) : data

    ExcelManager.getInstance().downloadExcel(filename, dataWithFormulae, wsName)

    setIsProcessingExport(false)
  }

  const generateFormula = (employeeWeights: IdvWeightDto[], employeeIndex: number) => {
    let prodList = ''
    let sumOfWeights = 0
    let andList = ''
    for (let i = 0; i < weightsColList.length; i++) {
      const weight = employeeWeights.find((w) => w.value === scoreGoalTypes[i])!.weight
      prodList += `${weightsColList[i]}${employeeIndex}*${weight}+`
      andList += `${weightsColList[i]}${employeeIndex}<>"",`
      sumOfWeights += weight
    }

    const condition = `AND(${andList.slice(0, andList.length - 1)})`

    const formula = `IF(${condition},(${prodList.slice(0, prodList.length - 1)})/${sumOfWeights},"")`
    return formula
  }

  const addFormulaeToData = (employeeData) => {
    return employeeData.map((row, index) => {
      if (index === 0) {
        return row
      } else {
        const employeeRow = row
        const employeeIndex = index + 1
        const currentLevel = employeeRow[4] as EmployeeLevelEnum
        const employeeWeights = weights.filter((w) => w.country === country && w.level === currentLevel)
        employeeRow[6] = { t: 'n', f: generateFormula(employeeWeights, employeeIndex), z: '0.00' }
        return employeeRow
      }
    })
  }

  const mapEmployeeWithScoreGridToArray = (employeeData: EmployeeWithLatestAndDraftEvaluation[]) => {
    const res: (string | number)[][] = []
    const header = headerColumnsNames
    res.push(header.filter((c) => !!c))

    for (const employee of employeeData) {
      res.push(
        isIdVWeightEnabled
          ? [
              employee.employee.id,
              employee.employee.firstName,
              employee.employee.lastName,
              employee.employee.buName,
              employee.employee.currentLevel,
              employee.latestEvaluation ? { t: 'n', v: employee.latestEvaluation.toFixed(2), z: '0.00' } : undefined,
              employee.draftEvaluation
                ? employee.draftEvaluation.toFixed(2)
                : employee.latestEvaluation
                ? employee.latestEvaluation.toFixed(2)
                : undefined,
              ...scoreGoalTypes.map((type) =>
                employee.scoreGoals ? employee.scoreGoals.find((sg) => sg.viewId === type)!.resultEvaluation : undefined,
              ),
            ]
          : [
              employee.employee.id,
              employee.employee.firstName,
              employee.employee.lastName,
              employee.employee.buName,
              employee.employee.currentLevel,
              ...scoreGoalTypes.map((type) =>
                employee.scoreGoals ? employee.scoreGoals.find((sg) => sg.viewId === type)!.resultEvaluation : undefined,
              ),
            ],
      )
    }

    return res
  }

  return (
    <>
      <BackgroundBox rt height="editImportExport" noPadding noShadow>
        <PerFlexRow align="middle" justify="end" noWrap className="fullHeight" mx="l">
          {isCompanyManager && (
            <PerButton
              text={t('global.button.import')}
              color="success"
              type="button"
              before="import_fill"
              onClick={(e) => {
                e.preventDefault()
                setIsImportModalOpened(true)
              }}
              disabled={isProcessingExport}
            />
          )}
          <PerButton
            text={t('global.button.export')}
            color="primary"
            type="button"
            before="download_fill"
            onClick={downloadExcel}
            style={{ marginLeft: '8px' }}
            isProcessing={{
              active: isProcessingExport,
            }}
          />
        </PerFlexRow>
      </BackgroundBox>

      <PerSpacer m="none" className="fullWidth noShadow" testid="spacer">
        <Divider
          type="horizontal"
          className="fullWidth"
          style={{ margin: '0px', padding: '0px', height: '0.1px', backgroundColor: 'light-gray', boxShadow: 'none' }}
        />
      </PerSpacer>

      <Table
        className={classNames({
          'light small noShadow noTopRadius scroll-compensation': true,
          viewWithScoreGridCollapseClosedAndExportImportBox: expanded,
          viewWithScoreGridCollapseOpenAndExportImportBox: !expanded,
        })}
        pagination={false}
        scroll={{ y: 'auto' }}
        dataSource={tableData.map((o) => ({ ...o, key: o.id }))}
        rowClassName={() => 'minHeight-row'}
        columns={columns}
        locale={{
          emptyText: <PerEmpty icon="employeesNo_stroke" heading={t('draftScoreGrid.table.noEmployees')} />,
        }}
      />

      {draftEvaluationModalVisible && employeeData && (
        <DraftScoreGridsModal
          onCancel={() => {
            setDraftEvaluationModalVisible(false)
            setEmployeeData(undefined)
          }}
          employeeData={employeeData!}
          companyId={companyId}
          country={country}
          isCompanyManager={isCompanyManager}
        />
      )}

      {latestEvaluationModalVisible && employeeData && (
        <LatestScoreGridsModal
          onCancel={() => {
            setLatestEvaluationModalVisible(false)
            setEmployeeData(undefined)
          }}
          employeeData={employeeData!}
          companyId={companyId}
          country={country}
        />
      )}

      {isImportModalOpened && (
        <DraftScoreGridsImportModal
          employeeIdsAndScoreGoals={tableData.map((el) => {
            return {
              employeeId: el.employee.id,
              scoreGoals: el.scoreGoals,
              hasDraft: el.existingDraftEvaluation,
              bu: el.businessUnit,
            }
          })}
          scoreGoalTypes={scoreGoalTypes}
          companyId={companyId}
          country={country}
          setIsOpened={setIsImportModalOpened}
        />
      )}
    </>
  )
}
